import React, { useEffect, useState } from "react";
import {
  Empty,
  Table,
  Button,
  Modal,
  Typography,
  Row,
  Col,
  DatePicker,
} from "antd";
import {
  LoadingOutlined,
  CalendarOutlined,
  TableOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { get_language, translate } from "../../locale";
import moment from "moment";
import UserServices from "../../services/UserServices";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import SecondaryButton from "../Buttons/SecondaryButton";
import FeedingServices from "../../services/FeedingServices";
import ImageGallery from "../Galleries/ImageGallery/ImageGallery";
import PrimaryButton from "../Buttons/PrimaryButton";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";

const localizer = momentLocalizer(moment);

const { Title, Text, Link } = Typography;

const WaterCalendar = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [date, setDate] = useState(
    moment().startOf("isoWeek").format("YYYY-MM-DD")
  );
  const [patient, setPatient] = useState(null);
  const [dataTable, setDataTable] = useState(null);
  const [modalDetails, setModalDetails] = useState(null);
  const [showAs, setShowAs] = useState("calendar");
  const [dateInterval, setDateInterval] = useState({ from: "", to: "" });

  const { username } = useParams();

  useEffect(() => {
    loadPatientInfo();
  }, []);

  useEffect(() => {
    getWaters();
    const restoreDates = () => {
      if (showAs === "calendar") {
        setDateInterval({ from: "", to: "" });
      }
    };

    restoreDates();
  }, [date, showAs]);

  const loadPatientInfo = async () => {
    try {
      const res = await UserServices.getProfile(username);
      if (res.status >= 200 && res.status < 300) {
        setPatient(res.data.my_contact_info);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getWaters = async () => {
    setLoading(true);
    try {
      let res = await FeedingServices.getWater(
        username,
        dateInterval.from !== "" ? dateInterval.from : date,
        dateInterval.to
      );
      if (res.status >= 200 && res.status < 300) {
        // filtro medicinas tomadas
        let newData = res.data.controls.map((i) => {
          const measureDate = `${i.measure_date} ${i.measure_time}`;
          const newDate =
            i.measure_time.indexOf("00:00:00") === -1
              ? new Date(measureDate)
              : new Date(measureDate.replace("00:00:00", "00:00:01"));

          return {
            ...i,
            start: newDate,
            end: newDate,
            allDay: false,
            date_time: measureDate,
            liters: i.answers
              ? i.answers.reduce((a, b) => a + parseFloat(b.agua), 0)
              : 0,
            type: "cell",
          };
        });

        setDataTable(newData);
        let litersData = {};
        newData.forEach((i) => {
          const date = moment(i.date_time).format("YYYY-MM-DD");
          const tz = i.date_time.substring(19, 25);
          if (!litersData[date]) {
            litersData = {
              ...litersData,
              [date]: {
                liters: i.liters,
                date_time: date + "T00:00:00" + tz,
              },
            };
          } else {
            const liters =
              parseFloat(litersData[date].liters) + parseFloat(i.liters);
            litersData[date] = { ...litersData[date], liters: liters };
          }
        });

        const litersLabels = Object.keys(litersData).map((i, k) => {
          return {
            id: "dsg_" + k,
            title: litersData[i].liters,
            start: new Date(litersData[i].date_time),
            end: new Date(litersData[i].date_time),
            allDay: true,
            date_time: litersData[i].date_time,
            liters: litersData[i].liters,
            type: "header",
          };
        });

        // agregamos los labels
        newData = [...newData, ...litersLabels];
        setData(newData);
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const antIcon = (
    <LoadingOutlined
      style={{
        fontSize: 24,
      }}
      spin
    />
  );

  const columns = [
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex space-x-4 justify-center">
          <Button
            type="text"
            icon={<EyeOutlined />}
            size="small"
            className="border-0"
            onClick={() => handleDetails(record)}
          />
        </div>
      ),
    },
    {
      title: "Fecha",
      dataIndex: "measure_date",
      key: "measure_date",
      render: (_, record) =>
        moment(record.measure_date).format(
          get_language === "en" ? "MM/DD/YYYY" : "DD/MM/YYYY"
        ),
      sorter: (a, b) => {
        if (a.measure_date < b.measure_date) return -1;
        if (a.measure_date > b.measure_date) return 1;
        return 0;
      },
    },
    {
      title: "Hora",
      dataIndex: "measure_time",
      key: "measure_time",
      render: (_, record) => record.measure_time,
      sorter: (a, b) => {
        if (a.measure_time < b.measure_time) return -1;
        if (a.measure_time > b.measure_time) return 1;
        return 0;
      },
    },
    {
      title: "Carga de agua",
      dataIndex: "liters",
      key: "liters",
      render: (_, record) =>
        record.liters !== "undefined"
          ? `${record.liters} lts.`
          : translate("N/D"),
      sorter: (a, b) => {
        if (a.liters < b.liters) return -1;
        if (a.liters > b.liters) return 1;
        return 0;
      },
    },
  ];

  const handleHideDetails = () => {
    setModalDetails(null);
  };
  const handlePrev = () => {
    setDate((v) => moment(v).subtract(7, "days").format("YYYY-MM-DD"));
    setDateInterval({ from: "", to: "" });
  };
  const handleToday = () => {
    setDate((v) => moment().startOf("isoWeek").format("YYYY-MM-DD"));
    setDateInterval({ from: "", to: "" });
  };
  const handleNext = () => {
    if (date !== moment().format("YYYY-MM-DD")) {
      setDate((v) => moment(v).add(7, "days").format("YYYY-MM-DD"));
      setDateInterval({ from: "", to: "" });
    }
  };
  const handleDetails = (item) => {
    setModalDetails(item);
  };

  const handleDateIntervalChange = (date, value) => {
    setDateInterval((prevValues) => ({
      ...prevValues,
      [value]: moment(date).format("YYYY-MM-DD"),
    }));
  };

  return (
    <Row className="w-full">
      <Col flex="column" className="w-full" id="Water">
        <Row className="w-full">
          <Col span={24} className="mt-4">
            <Title level={2} className="text-center">
              {translate("Carga de agua de ")}{" "}
              {patient && `${patient.user.full_name}`}
            </Title>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col span={24} className="flex flex-row justify-end space-x-2">
            <span className="flex flex-col justify-center">
              {translate("Formato")}:
            </span>
            <Button
              icon={<CalendarOutlined />}
              onClick={() => setShowAs("calendar")}
              type={showAs === "calendar" ? "primary" : "default"}
            />
            <Button
              icon={<TableOutlined />}
              onClick={() => setShowAs("table")}
              type={showAs === "table" ? "primary" : "default"}
            />
          </Col>
        </Row>
        {showAs === "calendar" && (
          <Calendar
            localizer={localizer}
            defaultDate={null}
            defaultView={Views.WEEK}
            views={["week"]}
            events={data || []}
            startAccessor="start"
            endAccessor="end"
            step={60}
            timeslots={6}
            style={{ height: 500 }}
            components={{
              toolbar: (props) => (
                <div className="flex flex-row my-4">
                  <div>
                    <SecondaryButton
                      className="mr-4"
                      onClick={() => {
                        props.onNavigate("PREV");
                        handlePrev();
                      }}
                    >
                      &lt;
                    </SecondaryButton>
                    <SecondaryButton
                      className="mr-4"
                      onClick={() => {
                        props.onNavigate("TODAY");
                        handleToday();
                      }}
                    >
                      Hoy
                    </SecondaryButton>
                    <SecondaryButton
                      className=""
                      onClick={() => {
                        props.onNavigate("NEXT");
                        handleNext();
                      }}
                    >
                      &gt;
                    </SecondaryButton>
                  </div>
                  <div className="flex flex-1 flex-row justify-center items-center">
                    {props.label}
                  </div>
                </div>
              ),
              week: {
                event: ({ event }) => {
                  return (
                    <Link
                      className={`text-center w-full block !text-white ${
                        event.type === "cell" ? "" : "rbc-event-content-header"
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        if (event.type === "cell") {
                          handleDetails(event);
                        }
                      }}
                    >
                      {event.start.toString().indexOf("00:00:00") === -1
                        ? moment(event.start).format("HH:mm")
                        : event.title}
                    </Link>
                  );
                },
              },
            }}
            formats={{
              eventTimeRangeFormat: () => {
                return "";
              },
              timeGutterFormat: (date, culture, localizer) => {
                const time = localizer.format(date, "HH:mm", culture);
                let rs = "";
                switch (time) {
                  case "00:00":
                    rs = "";
                    break;
                  case "06:00":
                    rs = "";
                    break;
                  case "12:00":
                    rs = "";
                    break;
                  case "18:00":
                    rs = "";
                    break;
                }
                return rs;
              },
            }}
          />
        )}
        {showAs === "table" && (
          <>
            <div className="flex flex-row my-4">
              <div>
                <SecondaryButton
                  className="mr-4"
                  onClick={() => {
                    handlePrev();
                  }}
                >
                  &lt;
                </SecondaryButton>
                <SecondaryButton
                  className="mr-4"
                  onClick={() => {
                    handleToday();
                  }}
                >
                  Hoy
                </SecondaryButton>
                <SecondaryButton
                  className=""
                  onClick={() => {
                    handleNext();
                  }}
                >
                  &gt;
                </SecondaryButton>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="mb-4 flex items-center">
                <label htmlFor="datePickerFrom" className="w-14">
                  Desde{" "}
                </label>
                <DatePicker
                  onChange={(date) => handleDateIntervalChange(date, "from")}
                  format={"YYYY/MM/DD"}
                  value={dateInterval.from ? moment(dateInterval.from) : null}
                  allowClear={false}
                />
              </div>
              <div className="flex items-center">
                <label htmlFor="datePickerTo" className="w-14">
                  Hasta{" "}
                </label>
                <DatePicker
                  onChange={(date) => handleDateIntervalChange(date, "to")}
                  format={"YYYY/MM/DD"}
                  value={dateInterval.to ? moment(dateInterval.to) : null}
                  allowClear={false}
                />
              </div>
              <PrimaryButton
                className="w-20 mt-4 text-center"
                disabled={dateInterval.from === "" || dateInterval.to === ""}
                onClick={() => {
                  getWaters();
                }}
              >
                Buscar
              </PrimaryButton>
            </div>

            <Table
              rowKey={(i) => i.id}
              className="w-full mt-6"
              dataSource={dataTable}
              columns={columns}
              loading={
                loading
                  ? { indicator: antIcon, className: "text-custom_blue" }
                  : false
              }
              locale={{
                emptyText: <Empty description={translate("No hay datos")} />,
                sortTitle: translate("Ordenar"),
                triggerDesc: translate("Ordenar (descendente)"),
                triggerAsc: translate("Ordenar (ascendente)"),
                cancelSort: translate("Cancelar orden"),
                filterTitle: translate("Filtros"),
                filterConfirm: translate("Aceptar"),
                filterReset: translate("Limpiar"),
                filterEmptyText: translate("No hay filtros"),
                filterCheckall: translate("Seleccinar todos"),
                filterSearchPlaceholder: translate("Buscar en filtros"),
              }}
            />
          </>
        )}
        <Modal
          width={600}
          open={modalDetails !== null}
          title={translate("Ingesta de agua")}
          onCancel={handleHideDetails}
          footer={[
            <Button key="ok" type="primary" onClick={handleHideDetails}>
              {translate("Aceptar")}
            </Button>,
          ]}
        >
          {modalDetails !== null && (
            <Row>
              <Col className="flex-1">
                <Row>
                  <Col>
                    <Text>
                      {moment(`${modalDetails.date_time}`)
                        .locale(get_language)
                        .format("dddd L HH:mm")}{" "}
                      {translate("Horas")}
                    </Text>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Text>
                      <strong>{translate("Cantidad")}:</strong>{" "}
                      <span className="text-gray-500">
                        {modalDetails.liters}
                      </span>{" "}
                      lts.
                    </Text>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Text>
                      <strong>{translate("Modo")}:</strong>{" "}
                      <span className="text-gray-500">
                        {modalDetails.mode === "manual"
                          ? "Manual"
                          : "Dispositivo"}
                      </span>
                    </Text>
                  </Col>
                </Row>
              </Col>

              <Col span={10}>
                <ImageGallery data={modalDetails.image} />
              </Col>
            </Row>
          )}
        </Modal>
      </Col>
    </Row>
  );
};

WaterCalendar.propTypes = {
  label: PropTypes.string,
  onNavigate: PropTypes.func,
};

export default WaterCalendar;
